import { useState, useEffect, useCallback } from 'react';
// @ts-ignore
import { useBlockSlot, SectionContextProvider, EditableToken } from '@vibesites/runtime/react';
// @ts-ignore
import type { ComponentContentSchema } from '@vibesites/runtime';
// @ts-ignore
import { Dialog, DialogContent, DialogTitle } from '@vibesites/ui-primitives';

/** Custom event name used to open the tour scheduler modal from any component. */
export const TOUR_SCHEDULER_EVENT = 'open-tour-scheduler';

/** Dispatch this from any "Schedule a Tour" button to open the modal. */
export function openTourScheduler() {
  window.dispatchEvent(new CustomEvent(TOUR_SCHEDULER_EVENT));
}

/**
 * Tour Scheduler Modal — invisible section that renders a Dialog overlay
 * containing the multihub-tour-scheduler block.
 *
 * Add this section to any property page that has "Schedule a Tour" buttons.
 * The buttons should call `openTourScheduler()` onClick.
 */
export function TourSchedulerModal() {
  const [open, setOpen] = useState(false);

  const tourSlot = useBlockSlot('tourschedulermodal.tour_block', {
    defaultValue: 'multihub-tour-scheduler',
  });

  const handleOpen = useCallback(() => setOpen(true), []);

  useEffect(() => {
    window.addEventListener(TOUR_SCHEDULER_EVENT, handleOpen);
    return () => window.removeEventListener(TOUR_SCHEDULER_EVENT, handleOpen);
  }, [handleOpen]);

  // Intercept clicks on #schedule-a-tour anchor links (from baked rich text content)
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = (e.target as HTMLElement).closest('a[href*="schedule-a-tour"]');
      if (target) {
        e.preventDefault();
        handleOpen();
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [handleOpen]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="sm:max-w-[550px] p-0 gap-0 border-0 overflow-hidden"
        style={{ borderRadius: '1rem' }}
      >
        <DialogTitle className="sr-only" style={{ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', borderWidth: 0 }}>
          Schedule a Tour
        </DialogTitle>
        <EditableToken path="tourschedulermodal.tour_block" type="block" label="Tour Scheduler Block" defaultValue={contentSchema.tokens['tourschedulermodal.tour_block'].defaultValue} as="div" style={{ display: 'contents' }}>
          {tourSlot ? (
            <SectionContextProvider sectionId={tourSlot.sectionId}>
              <tourSlot.BlockComponent sectionId={tourSlot.sectionId} />
            </SectionContextProvider>
          ) : null}
        </EditableToken>
      </DialogContent>
    </Dialog>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'TourSchedulerModal',
  displayName: 'Tour Scheduler Modal',
  tokens: {
    'tourschedulermodal.tour_block': {
      label: 'Tour Scheduler Block',
      type: 'block',
      defaultValue: 'multihub-tour-scheduler',
      acceptedBlockCategories: ['Forms'],
    },
  },
};

(TourSchedulerModal as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default TourSchedulerModal;
